import React, {useContext, useEffect, useMemo, useState} from 'react'
import {IntlProvider, useIntl} from "react-intl"
import {GetStatsTranslations, GetGeneralTranslations} from "./API";
import {formatTranslationsToIntl} from "./formats";
import {Loading} from "./App";

export const LocaleContext = React.createContext(null)

function determineInitialLocale(language, languageOptions) {
    if (localStorage.getItem("userLocale")) {
        return localStorage.getItem("userLocale")
    } else {
        const browserLanguage = navigator.language.split(/[-_]/)[0]
        if (languageOptions.includes(browserLanguage)) {
            return browserLanguage
        }
    }
    return language
}

export default function LocaleWrapper( {children, language = "en", languageOptions = ["en"]} ) {
    if (!languageOptions.includes("en")) {
        // Add "en" to all instances
        languageOptions.push("en")
    }
    const [locale, setLocale] = useState(() => determineInitialLocale(language, languageOptions))

    useEffect(() => {
        const userLocale = determineInitialLocale(language, languageOptions)
        setLocale(userLocale)
        document.documentElement.lang = userLocale
    }, [language, languageOptions])

    const getTranslations = GetStatsTranslations(locale)
    const getGeneralTranslations = GetGeneralTranslations(locale)

    const isError = getTranslations.isError || getGeneralTranslations.isError
    const isLoading = getTranslations.isLoading || getGeneralTranslations.isLoading
    const isSuccess = getTranslations.isSuccess && getGeneralTranslations.isSuccess

    const translations = useMemo(() => {
        if (isSuccess) {
            return {
                ...formatTranslationsToIntl(getGeneralTranslations.data),
                ...formatTranslationsToIntl(getTranslations.data)
            }
        }
        return {}
    }, [getGeneralTranslations.data, getTranslations.data, isSuccess])

    function changeLocale(lang) {
        setLocale(lang)
        localStorage.setItem("userLocale", lang)
        document.documentElement.lang = lang
    }

    return <LocaleContext.Provider value={{locale, changeLocale, languageOptions}}>
        {isError && <h1>Error in loading translations</h1>}
        {isLoading && <Loading />}
        {isSuccess &&
            <IntlProvider locale={locale} defaultLocale={'en'} key={locale} messages={translations} onError={()=>{}}>
                {children}
            </IntlProvider>
        }
    </LocaleContext.Provider>
}

export function LocaleSwitcher() {
    const intl = useIntl()
    const {locale, changeLocale, languageOptions} = useContext(LocaleContext)

    return (<div className={"locale-container"}>
            <select className={"locale-switcher"} value={locale} onChange={e=>changeLocale(e.target.value)}>
                {languageOptions.map((lang) => {
                    return <option value={lang} key={lang}>
                        {intl.formatMessage({
                            defaultMessage: lang,
                            id: lang
                        })}
                    </option>
                })}
            </select>
        </div>
    )
}